if (!customElements.get('image-gallery-modal')) {
  customElements.define(
    'image-gallery-modal',
    class ImageGalleryModal extends ModalDialog {
      constructor() {
        super();
        this.images = [];
        this.currentIndex = 0;
        this.prevButton = null;
        this.nextButton = null;
      }

      connectedCallback() {
        super.connectedCallback();
        
        // Override the pointerup handler to exclude nav buttons and toggle
        if (this.classList.contains('media-modal')) {
          // Remove the default pointerup listener by replacing it
          const newPointerUpHandler = (event) => {
            if (event.pointerType === 'mouse' && 
                !event.target.closest('deferred-media, product-model, .image-gallery-modal__nav, .image-gallery-modal__toggle')) {
              this.hide();
            }
          };
          this.addEventListener('pointerup', newPointerUpHandler);
        }
        
        this.setupNavigation();
      }

      getAllImages() {
        // Always query from the content container to ensure we get all images
        const content = this.querySelector('.image-gallery-modal__content');
        if (content) {
          return Array.from(content.querySelectorAll('.image-gallery-modal__item[data-media-id]'));
        }
        return Array.from(this.querySelectorAll('.image-gallery-modal__item[data-media-id]'));
      }

      setupNavigation() {
        // Get all images
        this.images = this.getAllImages();
        this.prevButton = this.querySelector('.image-gallery-modal__nav--prev');
        this.nextButton = this.querySelector('.image-gallery-modal__nav--next');

        if (this.prevButton) {
          // Remove existing listeners if any
          const newPrevButton = this.prevButton.cloneNode(true);
          this.prevButton.parentNode.replaceChild(newPrevButton, this.prevButton);
          this.prevButton = newPrevButton;

          this.prevButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.showPrevious();
            return false;
          }, true);
          
          this.prevButton.addEventListener('pointerup', (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.showPrevious();
            return false;
          }, true);
        }

        if (this.nextButton) {
          // Remove existing listeners if any
          const newNextButton = this.nextButton.cloneNode(true);
          this.nextButton.parentNode.replaceChild(newNextButton, this.nextButton);
          this.nextButton = newNextButton;

          this.nextButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.showNext();
            return false;
          }, true);
          
          this.nextButton.addEventListener('pointerup', (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.showNext();
            return false;
          }, true);
        }

        // Keyboard navigation
        const dialog = this.querySelector('[role="dialog"]');
        if (dialog) {
          dialog.addEventListener('keydown', (event) => {
            if (event.code === 'ArrowLeft') {
              event.preventDefault();
              event.stopPropagation();
              this.showPrevious();
            } else if (event.code === 'ArrowRight') {
              event.preventDefault();
              event.stopPropagation();
              this.showNext();
            }
          });
        }
      }

      show(opener) {
        super.show(opener);
        // Re-setup navigation in case modal was moved to body
        this.setupNavigation();
        this.showActiveMedia();
      }

      showActiveMedia() {
        const mediaId = this.openedBy.getAttribute('data-media-id');
        if (!mediaId) return;

        // Refresh images array to ensure we have all images
        this.images = this.getAllImages();

        // Remove active class from all items
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${mediaId}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });

        // Add active class to the clicked item
        const activeMedia = this.querySelector(`[data-media-id="${mediaId}"]`);
        if (activeMedia) {
          activeMedia.classList.add('active');
          
          // Update current index for navigation - find the exact match
          this.currentIndex = this.images.findIndex(img => img.getAttribute('data-media-id') === mediaId);
          if (this.currentIndex === -1) {
            // Fallback: try to find by element reference
            this.currentIndex = this.images.indexOf(activeMedia);
            if (this.currentIndex === -1) this.currentIndex = 0;
          }

          // Scroll to active media after a brief delay to ensure layout is ready
          setTimeout(() => {
            const container = this.querySelector('[role="document"]');
            if (container) {
              if (window.innerWidth >= 750) {
                // Desktop: scroll horizontally
                container.scrollLeft = activeMedia.offsetLeft - (container.clientWidth / 2) + (activeMedia.offsetWidth / 2);
              } else {
                // Mobile: scroll into view
                activeMedia.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }
          }, 50);
        }

        this.updateNavigation();
      }

      showPrevious() {
        // Refresh images array to ensure we have all images
        this.images = this.getAllImages();
        if (this.images.length === 0) return;
        
        this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
        const prevMedia = this.images[this.currentIndex];
        if (prevMedia) {
          this.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('active'));
          prevMedia.classList.add('active');
          setTimeout(() => {
            const container = this.querySelector('[role="document"]');
            if (container) {
              if (window.innerWidth >= 750) {
                // Desktop: scroll horizontally
                container.scrollLeft = prevMedia.offsetLeft - (container.clientWidth / 2) + (prevMedia.offsetWidth / 2);
              } else {
                // Mobile: scroll into view
                prevMedia.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }
          }, 50);
        }
      }

      showNext() {
        // Refresh images array to ensure we have all images
        this.images = this.getAllImages();
        if (this.images.length === 0) return;
        
        this.currentIndex = (this.currentIndex + 1) % this.images.length;
        const nextMedia = this.images[this.currentIndex];
        if (nextMedia) {
          this.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('active'));
          nextMedia.classList.add('active');
          setTimeout(() => {
            const container = this.querySelector('[role="document"]');
            if (container) {
              if (window.innerWidth >= 750) {
                // Desktop: scroll horizontally
                container.scrollLeft = nextMedia.offsetLeft - (container.clientWidth / 2) + (nextMedia.offsetWidth / 2);
              } else {
                // Mobile: scroll into view
                nextMedia.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }
          }, 50);
        }
      }

      updateNavigation() {
        if (this.prevButton) {
          this.prevButton.style.display = this.images.length > 1 ? 'flex' : 'none';
        }
        if (this.nextButton) {
          this.nextButton.style.display = this.images.length > 1 ? 'flex' : 'none';
        }
      }

      hide() {
        super.hide();
      }
    }
  );
}

